import {usePageContext}                                                      from '@/context/PageContext'
import {useFrame, useThree}                                                  from '@react-three/fiber'
import {useEffect, useMemo}                                                  from 'react'
import * as THREE                                                            from 'three'
import {clamp, Fn, positionLocal, positionWorld, texture, uniform, uv, vec4} from 'three/src/Three.TSL'

export const usePortfolioMaterial = ({
	project,
	progress,
	inView,
}) => {
	const {camera} = useThree()
	const {sizes}  = usePageContext()

	// region Uniforms
	const uProgress           = useMemo(() => uniform(0), [])
	const uDistortionProgress = useMemo(() => uniform(0), [])
	const uDistortionFactor   = useMemo(() => uniform(2), [])
	const uProjectOpacity     = useMemo(() => uniform(1), [])
	const uTime               = useMemo(() => uniform(0), [])
	// endregion Uniforms

	useFrame(() => {
		if (!inView.current) {
			return
		}

		uProgress.value               = progress.current
		const distortionProgressDelay = .5
		uDistortionProgress.value     = Math.min(Math.max(0, (progress.current - distortionProgressDelay) / (1 - distortionProgressDelay)), 1)

		const projectOpacityDelay = .9
		const projectOpacityValue = Math.min(Math.max(0, 1 - (progress.current - projectOpacityDelay) / (1 - projectOpacityDelay)), 1)
		uProjectOpacity.value     = projectOpacityValue
		if (projectOpacityValue < 1 && !project.isCenterElement) {
			uProjectOpacity.value = 0
		}

		// Move camera forward
		const cameraDefaultZPosition = 5
		camera.position.z            = cameraDefaultZPosition - progress.current * 4.5
	})
	// endregion Progress

	// region Sizes
	useEffect(() => {
		const invertedRatio     = sizes[1] / sizes[0] || 0
		uDistortionFactor.value = 1.5 + invertedRatio
	}, [sizes])
	// endregion Sizes
	// endregion Handlers

	// region Data
	const mediaTexture = useMemo(() => new THREE.TextureLoader().load(project.media.url), [project.media.url])
	// endregion

	// region Nodes
	const positionNode = Fn(() => {
		const factor = clamp(0, positionWorld.z.div(6).add(1), 1)

		positionLocal.z.addAssign(positionWorld.x.mul(positionWorld.x).mul(uDistortionFactor.mul(factor)).mul(uDistortionProgress))
		return positionLocal
	})()

	const colorNode = Fn(() => {
		return vec4(texture(mediaTexture, uv()).rgb, uProjectOpacity)
	})()
	// endregion

	return {
		key: uTime.id,
		colorNode,
		positionNode,
	}
}
